import { createFileRoute, useNavigate, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/use-language";
import { useSubscription } from "@/hooks/useSubscription";
import { sounds } from "@/lib/sounds";
import { ChildFavorites } from "@/components/ChildFavorites";
import { BadgeCatalogGrid } from "@/components/BadgeCatalogGrid";
import { ForYouShelf } from "@/components/ForYouShelf";
import { FeaturedShelf } from "@/components/FeaturedShelf";
import { AiRecommendationsShelf } from "@/components/AiRecommendationsShelf";
import { StreakHero } from "@/components/StreakHero";
import { FeaturedCharacterCard } from "@/components/FeaturedCharacterCard";
import { AmbientControl } from "@/components/AmbientControl";
import { OfflineBadge } from "@/components/OfflineBadge";
import { useOfflineCache } from "@/hooks/use-offline-cache";
import { useSmartBack } from "@/hooks/use-smart-back";
import { evaluateBadges } from "@/lib/badges.functions";
import { STORIES } from "@/lib/content";
import type { BadgeMetrics } from "@/lib/badges";
import { toast } from "sonner";
import {
  ArrowLeft,
  BookOpen,
  Video,
  Palette,
  Target,
  Gift,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/child/$childId")({
  component: ChildRouteShell,
});

type Child = {
  id: string;
  name: string;
  age: number;
  age_tier: string;
  avatar_emoji: string;
  total_points: number;
  current_streak: number;
  last_active_at: string | null;
};

type EarnedBadge = { id: string; code: string; label_en: string; label_ar: string; emoji: string; earned_at: string };

function ChildRouteShell() {
  const { childId } = Route.useParams();
  const { pathname } = useLocation();
  const isHome = pathname === `/app/child/${childId}`;

  return isHome ? <ChildHome /> : <Outlet />;
}

function ChildHome() {
  const { childId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useSmartBack(() => navigate({ to: "/app" }));
  const { lang, t } = useLang();
  const { subscription } = useSubscription();
  const isAr = lang === "ar";
  const evaluate = useServerFn(evaluateBadges);
  // Quiet offline orchestrator — does nothing for free tier.
  const offline = useOfflineCache(childId);
  const [child, setChild] = useState<Child | null>(null);
  const [earned, setEarned] = useState<EarnedBadge[]>([]);
  const [completedStoryIds, setCompletedStoryIds] = useState<Set<string>>(new Set());
  const [metrics, setMetrics] = useState<BadgeMetrics>({
    total_points: 0, current_streak: 0, stories_completed: 0, challenges_done: 0,
    coloring_done: 0, videos_watched: 0, theme_counts: {},
  });
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    const [{ data: c }, { data: b }, { data: evs }] = await Promise.all([
      supabase.from("children").select("id, name, age, age_tier, avatar_emoji, total_points, current_streak, last_active_at").eq("id", childId).single(),
      supabase.from("badges").select("id, code, label_en, label_ar, emoji, earned_at").eq("child_id", childId).order("earned_at", { ascending: false }),
      supabase.from("progress_events").select("activity_type, activity_id").eq("child_id", childId),
    ]);
    if (c) setChild(c as Child);
    setEarned((b as EarnedBadge[]) ?? []);
    const ev = evs ?? [];
    const themeCounts: BadgeMetrics["theme_counts"] = {};
    const completed = new Set<string>();
    for (const e of ev) {
      if (e.activity_type !== "story" || !e.activity_id) continue;
      completed.add(e.activity_id);
      const s = STORIES.find((x) => x.id === e.activity_id);
      if (s) themeCounts[s.theme] = (themeCounts[s.theme] ?? 0) + 1;
    }
    setCompletedStoryIds(completed);
    setMetrics({
      total_points: c?.total_points ?? 0,
      current_streak: c?.current_streak ?? 0,
      stories_completed: ev.filter((e) => e.activity_type === "story").length,
      challenges_done: ev.filter((e) => e.activity_type === "challenge").length,
      coloring_done: ev.filter((e) => e.activity_type === "coloring").length,
      videos_watched: ev.filter((e) => e.activity_type === "video").length,
      theme_counts: themeCounts,
    });
  };

  useEffect(() => { load(); }, [childId]);

  // Streak bump: if last_active_at is not today, increment streak (or reset if gap > 1 day).
  const bumpStreak = async (c: Child) => {
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    const last = c.last_active_at ? new Date(c.last_active_at) : null;
    const lastDay = last ? last.toISOString().slice(0, 10) : null;
    if (lastDay === today) return; // already counted today
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yDay = yesterday.toISOString().slice(0, 10);
    const newStreak = lastDay === yDay ? (c.current_streak ?? 0) + 1 : 1;
    await supabase
      .from("children")
      .update({ current_streak: newStreak, last_active_at: now.toISOString() })
      .eq("id", c.id);
    if (newStreak > (c.current_streak ?? 0)) {
      // No streak sound — gamification is intentionally silent (docs/AUDIO_PLAN.md §5).
      toast.success(
        isAr ? `🔥 سلسلة ${newStreak} يوم!` : `🔥 ${newStreak}-day streak!`,
        { duration: 3500 },
      );
    }
  };

  const activeToday = useMemo(() => {
    if (!child?.last_active_at) return false;
    return new Date(child.last_active_at).toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10);
  }, [child?.last_active_at]);

  const earnedCodes = useMemo(() => new Set(earned.map((e) => e.code)), [earned]);

  const quickLog = async (type: "video" | "coloring", points: number, label: string) => {
    if (!child) return;
    setBusy(type);
    sounds.tap();
    const { error } = await supabase.from("progress_events").insert({
      child_id: child.id, activity_type: type, points_earned: points,
    });
    if (error) { toast.error(error.message); setBusy(null); return; }
    toast.success(`+${points} · ${label}`);
    await bumpStreak(child);
    try {
      const res = await evaluate({ data: { childId: child.id } });
      if (res.ok) {
        for (const nb of res.earned) {
          // Badge earn = keepsake → settle (controller rate-limits multi-badge bursts).
          sounds.settle();
          toast.success(`🎉 ${nb.emoji} ${isAr ? nb.label_ar : nb.label_en}`, { duration: 4000 });
        }
      }
    } catch {}
    await load();
    setBusy(null);
  };

  if (!child) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="size-10 rounded-full border-4 border-clay border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-5 py-6 max-w-2xl mx-auto">
      <div className="mb-4 flex items-center justify-between gap-3">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-sm font-bold text-foreground/60 hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> {t("chooseProfile")}
        </button>
        <AmbientControl />
      </div>

      <StreakHero
        name={child.name}
        avatar={child.avatar_emoji}
        points={child.total_points}
        streak={child.current_streak}
        activeToday={activeToday}
        earnedCodes={earnedCodes}
        metrics={metrics}
        isAr={isAr}
      />

      {/* First story within 3 minutes: prominent CTA for brand-new profiles */}
      {completedStoryIds.size === 0 && (
        <div className="mb-6 rounded-3xl bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-primary/40 p-5 text-center">
          <p className="text-sm text-foreground/70 mb-2">
            {isAr ? "حان وقت مغامرتك الأولى!" : "Time for your first adventure!"}
          </p>
          <Link
            to="/app/child/$childId/stories"
            params={{ childId }}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-2xl font-bold shadow-press active:translate-y-0.5 active:shadow-none transition-all"
          >
            <BookOpen className="size-5" />
            {isAr ? "اقرأ أول قصة" : "Read your first story"}
          </Link>
        </div>
      )}

      {/* Guided weekly journey — top-of-home entry point */}
      <Link
        to="/app/child/$childId/journey"
        params={{ childId }}
        className="block mb-6 rounded-3xl bg-gradient-to-br from-primary/15 via-accent/10 to-background border-2 border-primary/30 p-5 hover:-translate-y-0.5 transition-all"
      >
        <div className="flex items-center gap-4">
          <div className="size-14 rounded-2xl bg-card flex items-center justify-center text-3xl shadow-md">🌱</div>
          <div className="flex-1">
            <p className={`${isAr ? "font-display-ar" : "font-display-en"} text-lg text-primary mb-0.5`} dir={isAr ? "rtl" : "ltr"}>
              {isAr ? "خطوتنا الصغيرة معًا" : "Our next small step together"}
            </p>
            <p className="text-sm text-foreground/70">
              {isAr ? "بهدوء، يومًا بيوم" : "Quietly, day by day"}
            </p>
          </div>
        </div>
      </Link>

      {/* Quiet Corner — evergreen calm layer, always present, no pressure */}
      <Link
        to="/app/child/$childId/quiet"
        params={{ childId }}
        className="block mb-6 rounded-3xl bg-card/60 border border-border/60 px-5 py-4 hover:border-primary/40 hover:-translate-y-0.5 transition-all"
      >
        <div className="flex items-center gap-4">
          <div className="size-11 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center text-2xl">🤍</div>
          <div className="flex-1">
            <p className={`${isAr ? "font-display-ar" : "font-display-en"} text-base text-primary mb-0.5`} dir={isAr ? "rtl" : "ltr"}>
              {isAr ? "ركن هادئ" : "Quiet Corner"}
            </p>
            <p className="text-xs text-foreground/60" dir={isAr ? "rtl" : "ltr"}>
              {isAr ? "مساحة دافئة صغيرة، متى أردت" : "A small warm space, whenever you need it"}
            </p>
          </div>
        </div>
      </Link>


      {/* Soft offline indicator — only appears for paid tiers; "Ready for your journey". */}
      {offline.status !== "no_subscription" && offline.status !== "idle" && (
        <div className="mb-6 flex items-center justify-between gap-3 rounded-2xl bg-card/60 border border-border/60 px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-xl">🧳</span>
            <p className="text-sm text-foreground/80" dir={isAr ? "rtl" : "ltr"}>
              {isAr ? "جذوري جاهز لرحلتكم" : "Jozori is ready for your journey"}
            </p>
          </div>
          <OfflineBadge state={offline.status} expiresAt={offline.licenseExpiresAt} />
        </div>
      )}

      <FeaturedCharacterCard isAr={isAr} childName={child.name} />

      <FeaturedShelf childId={childId} isAr={isAr} ageTier={child.age_tier} />

      <AiRecommendationsShelf childId={childId} childName={child.name} isAr={isAr} />

      <ForYouShelf
        childId={childId}
        childName={child.name}
        ageTier={child.age_tier}
        completedStoryIds={completedStoryIds}
        userTier={(subscription?.tier ?? "free") as "free" | "family" | "premium"}
        isAr={isAr}
      />


      {/* Primary adventures */}
      <h2 className={`${isAr ? "font-display-ar" : "font-display-en"} text-xl font-bold mb-4 tracking-tight`}>
        {t("todaysAdventures")}
      </h2>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <BigCard to="/app/child/$childId/stories" params={{ childId }} icon={<BookOpen className="size-6" />} ar="قصص" en={t("stories")} color="bg-primary/15 text-primary" isAr={isAr} />
        <BigCard to="/app/child/$childId/challenges" params={{ childId }} icon={<Target className="size-6" />} ar="تحدي اليوم" en={t("challenge")} color="bg-secondary/15 text-secondary" isAr={isAr} />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <QuickBtn onClick={() => quickLog("video", 15, t("videos"))} disabled={busy === "video"} icon={<Video className="size-5" />} ar="فيديو" en={t("videos")} pts={15} isAr={isAr} />
        <QuickBtn onClick={() => quickLog("coloring", 10, t("coloring"))} disabled={busy === "coloring"} icon={<Palette className="size-5" />} ar="تلوين" en={t("coloring")} pts={10} isAr={isAr} />
      </div>

      <Link
        to="/app/child/$childId/rewards"
        params={{ childId }}
        className="block bg-gradient-to-br from-accent/20 to-primary/15 border-2 border-accent/40 rounded-3xl p-5 mb-8 flex items-center gap-4 hover:-translate-y-0.5 transition-all"
      >
        <div className="size-14 rounded-2xl bg-card flex items-center justify-center text-3xl shadow-md">🎁</div>
        <div className="flex-1">
          <p className="font-display-ar text-lg text-primary mb-0.5" dir="rtl">متجر المكافآت</p>
          <h3 className="font-bold text-sm">{t("shop")}</h3>
        </div>
        <Gift className="size-5 text-primary" />
      </Link>

      <ChildFavorites childId={childId} />

      <div className="mt-2">
        <BadgeCatalogGrid metrics={metrics} earned={earned} isAr={isAr} />
      </div>
    </div>
  );
}

function BigCard({
  to, params, icon, ar, en, color, isAr,
}: {
  to: "/app/child/$childId/stories" | "/app/child/$childId/challenges";
  params: { childId: string };
  icon: React.ReactNode; ar: string; en: string; color: string; isAr: boolean;
}) {
  return (
    <Link to={to} params={params} className="bg-card border-2 border-clay/30 rounded-3xl p-5 hover:border-primary hover:-translate-y-1 transition-all">
      <div className={`size-12 rounded-2xl ${color} flex items-center justify-center mb-3`}>{icon}</div>
      <p className={`${isAr ? "font-display-ar" : "font-display-ar"} text-lg text-primary mb-0.5`} dir="rtl">{ar}</p>
      <h3 className="font-bold text-sm">{en}</h3>
    </Link>
  );
}

function QuickBtn({
  onClick, disabled, icon, ar, en, pts, isAr,
}: {
  onClick: () => void; disabled?: boolean; icon: React.ReactNode;
  ar: string; en: string; pts: number; isAr: boolean;
}) {
  return (
    <button onClick={onClick} disabled={disabled} className="bg-card border-2 border-clay/30 rounded-3xl p-4 text-left hover:border-primary hover:-translate-y-0.5 transition-all disabled:opacity-50">
      <div className="size-10 rounded-xl bg-accent/15 text-accent flex items-center justify-center mb-2">{icon}</div>
      <p className={`${isAr ? "font-display-ar" : "font-display-ar"} text-base text-primary mb-0.5`} dir="rtl">{ar}</p>
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-xs">{en}</h3>
        <span className="text-xs font-bold text-foreground/60">+{pts}</span>
      </div>
    </button>
  );
}
